/**
 * The three identifiers one logical order carries, generated together.
 *
 * §6.3 is unambiguous and it is the exit criterion's whole substance:
 *
 * > **One logical order = one `client_order_id` = one `Idempotency-Key` = one
 * > `nonce_ms`.** Generate all three together, store them together, and reuse
 * > all three on every retry of that same order.
 *
 * They are three different mechanisms with three different jobs, and a client
 * that treats any two of them as interchangeable either gets rejected or places
 * a second order:
 *
 * | | Prevents | Scope |
 * |---|---|---|
 * | `X-UD-Nonce` (`nonce_ms`) | replay of a **signature** | ring of 256 per key, 30 s past / 5 s future |
 * | `Idempotency-Key` | duplicate **effects** from a retried request | `(account, endpoint)`, 120 s on the order endpoints |
 * | `client_order_id` | a duplicate **order** | unique per account **within its market** (§F106); minted fresh per order regardless |
 *
 * Generating them in one place, as one object, is what makes "reuse all three"
 * a single decision rather than three that have to agree.
 *
 * No viem here on purpose: this file is reachable from the eager graph and
 * carries nothing that needs the signing stack (invariant 13).
 */

/** `[A-Za-z0-9_-]`, 1–64 characters (§2.4). */
const CLIENT_ORDER_ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/

export interface OrderIdentity {
  /** Unique per account within its market, and the handle the WS frames carry back. */
  clientOrderId: string
  /** 16–128 characters (§6.1). A POST without one is a `400`. */
  idempotencyKey: string
  /** Unix ms at signing. **Must equal `X-UD-Nonce` exactly** (§3.4.2). */
  nonceMs: number
}

/**
 * Mint one order's identity.
 *
 * `nonceMs` comes from the **corrected** clock, floored: `Clock.now()` is
 * `performance.now() + offset` and carries sub-millisecond precision, which
 * `BigInt()` refuses outright and which would not be an integer epoch
 * millisecond even if it did not. The same flooring bug was found in
 * `approvalNonce` during Phase 5 and it is the same fix.
 */
export function newOrderIdentity(nowMs: number, random: () => string = randomSuffix): OrderIdentity {
  const nonceMs = Math.floor(nowMs)
  // The prefix is the surface, the nonce is the minute, the suffix breaks the
  // tie between two tabs signing in the same millisecond — which is a real case,
  // because IndexedDB is per origin and both tabs share one key (§3.4.3).
  const clientOrderId = `web-${nonceMs}-${random()}`

  // Checked rather than assumed, because this value is enforced **forever** per
  // account (§6.3): an id the server refuses as malformed is not a rejected
  // order, it is an identity permanently burned by a client that never looked.
  // The generator is correct today; this is the guard for the day its shape
  // changes.
  if (!isValidClientOrderId(clientOrderId)) {
    throw new Error(`generated an invalid client_order_id: ${JSON.stringify(clientOrderId)}`)
  }

  return { clientOrderId, idempotencyKey: newIdempotencyKey(), nonceMs }
}

export function isValidClientOrderId(value: string): boolean {
  return CLIENT_ORDER_ID_PATTERN.test(value)
}

/**
 * Whether a signature minted at `nonceMs` is still inside the anti-replay
 * window (§3.4.3: `nonce_ms > now − 30000`).
 *
 * The margin exists because the check that matters happens at the *server*, and
 * a request that leaves at `T−29.9s` arrives after it. Re-signing early costs
 * nothing; arriving stale costs the retry.
 */
export const NONCE_WINDOW_MS = 30_000
export const NONCE_SAFETY_MARGIN_MS = 2_000

export function isNonceFresh(nonceMs: number, nowMs: number): boolean {
  return nowMs - nonceMs < NONCE_WINDOW_MS - NONCE_SAFETY_MARGIN_MS
}

// ---------------------------------------------------------------------------

function newIdempotencyKey(): string {
  const c: Crypto | undefined = globalThis.crypto
  if (c?.randomUUID) return c.randomUUID()
  if (c?.getRandomValues) return hex(c.getRandomValues(new Uint8Array(16)))
  // The same refusal `HttpClient` makes, for the same reason: a POST without an
  // `Idempotency-Key` is a `400`, and a defaulted key is not an idempotency key.
  throw new Error('no WebCrypto: cannot generate an Idempotency-Key')
}

function randomSuffix(): string {
  const c: Crypto | undefined = globalThis.crypto
  if (c?.getRandomValues) return hex(c.getRandomValues(new Uint8Array(4)))
  throw new Error('no WebCrypto: cannot generate a client_order_id')
}

function hex(bytes: Uint8Array): string {
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}
